if (!customElements.get('product-compare')) {
  // Where the table's scroll can drive the sticky summary in CSS, it moves on the compositor and keeps up on touch devices
  const stickyFollowsScroll = CSS.supports('animation-timeline', '--compare-x') && CSS.supports('timeline-scope', '--compare-x');

  class ProductCompare extends HTMLElement {
    constructor() {
      super();
      this.init = debounce(this.doInit.bind(this), 300);
      this.requestId = 0;
    }

    disconnectedCallback() {
      this.detachObservers();
      this.detachSticky();
      this.removeEventListener('change', this.productChangeHandler);
      window.removeEventListener('resizeend', this.resizeHandler);
      this.resizeHandler = null;
    }

    connectedCallback() {
      if (window.sectionInstances.has(this)) {
        // Moved in the DOM, e.g. by a popup moving itself to the body. The carousel drops its listeners when it's
        // disconnected and has nothing to add them back, so one that had already set itself up is set up again.
        const slider = this.querySelector('slider-component');
        if (slider?.slider) {
          slider.resizeObserver?.disconnect();
          slider.init();
        }

        if (this.products) {
          this.bindEvents();
          this.renderSticky();
        }

        return;
      }

      window.sectionInstances.set(this, true);

      // Dynamic comparisons wait for setProducts()
      if (this.dataset.dynamic) return;

      window.initScriptOnDemand(this, this.init.bind(this), 1500);
    }

    doInit() {
      this.fetchAttempts = 0;

      if (this.dataset.products) {
        this.products = this.dataset.products.split(',').map(Number);
      }

      if (this.products && this.products.length > 0) {
        this.excludeProduct = Number(this.dataset.excludeProduct);
        this.productLimit = this.products.length;
        this.searchUrl = this.dataset.searchUrl;

        this.bindEvents();
        this.fetchProducts();
      } else {
        this.setAttribute('data-compare-is-loading', 'false');
      }
    }

    bindEvents() {
      if (this.resizeHandler) return;

      this.resizeHandler = () => {
        this.checkRowHeights(0);
        this.layoutSticky();
      };
      window.addEventListener('resizeend', this.resizeHandler);

      this.productChangeHandler = this.productChangeHandler || this.handleProductSwitcherChange.bind(this);
      this.addEventListener('change', this.productChangeHandler);
    }

    setProducts(products) {
      this.fetchAttempts = 0;
      this.products = products.map(Number);
      this.productLimit = this.products.length;
      this.searchUrl = this.dataset.searchUrl;

      this.bindEvents();

      if (this.products.length) {
        this.fetchProducts();
      } else {
        this.requestId += 1;
        this.clearProducts();
      }
    }

    clearProducts() {
      this.detachSticky();
      this.querySelector('slider-component')?.remove();
      this.setAttribute('data-compare-is-loading', 'false');
      this.dispatchLoaded();
    }

    handleProductSwitcherChange(evt) {
      if (evt.target.matches('.js-compare-switcher')) {
        const oldProductId = Number(evt.target.dataset.originalProductId);
        const newProductId = Number(evt.target.value);

        if (!isNaN(newProductId)) {
          // Replace oldProductId within this.products with newProductId
          const index = this.products.indexOf(oldProductId);

          if (index !== -1) {
            this.products[index] = newProductId;
          } else {
            console.error(`Product ID ${oldProductId} not found in the array.`);
          }

          this.fetchProducts();
        }
      }
    }

    // Merges the choices the page worked out into the selects the section request rendered,
    // then drops duplicates and anything already sitting in a column. Additive on purpose:
    // whatever the section request managed to render is left in place.
    renderSwitcherOptions() {
      const switchers = this.querySelectorAll('.js-compare-switcher');
      if (!switchers.length) return;

      const source = this.querySelector('template.js-compare-switcher-options');
      const pageOptions = source ? Array.from(source.content.querySelectorAll('option')) : [];

      switchers.forEach(select => {
        pageOptions.forEach(option => select.appendChild(option.cloneNode(true)));

        const seen = new Set();

        Array.from(select.options).forEach(option => {
          const id = Number(option.value);

          // The first option is the product on show and carries no value
          if (!id) return;

          if (seen.has(id) || this.products.includes(id)) {
            option.remove();
          } else {
            seen.add(id);
          }
        });
      });
    }

    async fetchProducts() {
      const requestId = ++this.requestId;
      this.setAttribute('data-compare-is-loading', 'true');

      try {
        // Exclude specified product from list if it exists
        if (this.excludeProduct && this.products.includes(this.excludeProduct)) {
          this.products.splice(this.products.indexOf(this.excludeProduct), 1);
        }

        // Prepare the search query
        const searchQuery = this.products
          .map((product) => `id:${product}`)
          .slice(0, this.productLimit)
          .join(' OR ');

        const response = await fetch(`${this.searchUrl}&q=${searchQuery}`);
        if (!response.ok) throw new Error(response.status);

        // Fetch response template and insert sorted product items
        const tmpl = document.createElement('template');
        let responseText = await response.text();

        // A newer request has taken over
        if (requestId !== this.requestId) return;

        responseText = responseText.replace(/<template\b[^>]*>|<\/template>/g, '');
        tmpl.innerHTML = responseText;

        // Only the carousel is fetched. The section heading is rendered in the context of
        // the page itself, so replacing it here would drop any dynamic sources it uses.
        const newSlider = tmpl.content.querySelector('product-compare slider-component');
        if (newSlider) {
          const productIDs = this.products;
          const ul = newSlider.querySelector('ul.compare-grid');

          // Sort list items based on the order of product IDs, keeping any label column first
          const sortedListItems = [
            ...Array.from(ul.querySelectorAll(':scope > li:not([data-product-id])')),
            ...productIDs
              .map(id => Array.from(ul.querySelectorAll('li[data-product-id]'))
                .find(li => parseInt(li.getAttribute('data-product-id')) === id))
              .filter(Boolean)
          ];

          // Clear and append sorted list items
          const currentCompareGrid = this.querySelector('ul.compare-grid');
          const scrollLeft = currentCompareGrid?.scrollLeft || 0;

          ul.innerHTML = '';
          sortedListItems.forEach(li => ul.appendChild(li));

          if (this.initialized) {
            ul.querySelectorAll('.scroll-trigger').forEach(elem => elem.classList.remove('scroll-trigger'));
          }

          const currentSlider = this.querySelector('slider-component');

          if (currentSlider) {
            currentSlider.outerHTML = newSlider.outerHTML;
          } else {
            (this.querySelector('.product-compare-container') || this).insertAdjacentHTML('beforeend', newSlider.outerHTML);
          }

          this.querySelector('ul.compare-grid').scrollLeft = scrollLeft;

          this.renderSwitcherOptions();
          this.renderSticky();

          // Call external functions to render and reinitialize
          window.renderSelects();
          window.loadDesktopOnlyTemplates();
          window.stickersReinit?.();

          this.attachObservers();
          this.revealWhenLaidOut(requestId);

          this.initialized = true;
          this.dispatchLoaded();
        } else if (this.dataset.dynamic) {
          this.clearProducts();
        }
      } catch (error) {
        this.fetchAttempts += 1;

        if (this.fetchAttempts > 4) {
          console.log('Product compare section error:', error);
          this.setAttribute('data-compare-is-loading', 'false');
        } else if (requestId === this.requestId) {
          this.fetchProducts(); // Retry
        }
      } finally {
        this.checkRowHeights(3000);
      }
    }

    // The loading screen stays up until the rows line up, so customers don't see them jump
    async revealWhenLaidOut(requestId) {
      await Promise.all([
        this.waitForVisibleImages(1000),
        new Promise(resolve => setTimeout(resolve, 100))
      ]);

      if (requestId !== this.requestId) return;

      requestAnimationFrame(() => {
        if (requestId !== this.requestId) return;

        this.setRowHeights();
        this.setAttribute('data-compare-is-loading', 'false');
      });
    }

    waitForVisibleImages(timeout) {
      const list = this.querySelector('ul.compare-grid');
      if (!list) return Promise.resolve();

      const listRect = list.getBoundingClientRect();
      const images = Array.from(list.querySelectorAll('img')).filter(img => {
        if (img.complete) return false;

        const rect = img.getBoundingClientRect();
        return rect.right > listRect.left && rect.left < listRect.right && rect.top < window.innerHeight;
      });

      if (!images.length) return Promise.resolve();

      return Promise.race([
        Promise.all(images.map(img => new Promise(resolve => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        }))),
        new Promise(resolve => setTimeout(resolve, timeout))
      ]);
    }

    dispatchLoaded() {
      const products = Array.from(this.querySelectorAll('ul.compare-grid > li[data-product-id]')).map(column => ({
        id: Number(column.dataset.productId),
        title: column.dataset.productTitle,
        url: column.dataset.productUrl,
        image: column.dataset.productImage,
        price: column.dataset.productPrice
      }));

      this.dispatchEvent(new CustomEvent('product-compare:loaded', {
        bubbles: true,
        detail: { products }
      }));
    }

    debouncedCheckRowHeights = debounce(() => {
      this.checkRowHeights();
    }, 600);

    attachObservers() {
      // Images that load after the rows were measured change their height
      this.rowCheckHandler = this.rowCheckHandler || this.debouncedCheckRowHeights.bind(this);
      this.querySelectorAll('.compare-cell img').forEach(img => {
        if (!img.complete) img.addEventListener('load', this.rowCheckHandler, { once: true });
      });
    }

    detachObservers() {
      this.querySelectorAll('.compare-cell img').forEach(img => {
        img.removeEventListener('load', this.rowCheckHandler);
      });
    }

    // Function to set uniform row heights
    checkRowHeights(delay = 0) {
      if (delay === 0) {
        this.setRowHeights();
      } else {
        requestAnimationFrame(() => {
          setTimeout(this.setRowHeights.bind(this), delay);
        });
      }
    }

    setRowHeights() {
      // Measure the natural heights, without the ones set by the last pass
      this.querySelectorAll('.compare-cell').forEach(cell => {
        cell.style.height = 'auto';
      });

      const columns = Array.from(this.querySelectorAll('.section-grid > li'))
        .map(column => Array.from(column.querySelectorAll(':scope > .compare-cell')));

      if (columns[0]) {
        const rowCount = columns[0].length;

        // Loop through each row index
        for (let i = 0; i < rowCount; i++) {
          // Get all cells in the current row across columns
          const rowCells = columns.map(column => column[i]).filter(Boolean);

          // Find the maximum height in the row
          const maxHeight = Math.max(...rowCells.map(cell => cell.offsetHeight));

          // Set each cell in the row to the max height
          rowCells.forEach(cell => {
            cell.style.height = `${maxHeight}px`;
          });
        }
      }

      this.layoutSticky();
    }

    /* STICKY SUMMARY */

    renderSticky() {
      // Tablets scroll too far ahead of the page's scripts for the pinned summary to keep up
      if (!this.dataset.stickySummary || window.themeDevice.isTablet || !this.querySelector('ul.compare-grid')) return;

      const columns = Array.from(this.querySelectorAll('ul.compare-grid > li'));

      if (!this.sticky) {
        this.sticky = document.createElement('div');
        this.sticky.className = 'compare-sticky';
        this.sticky.setAttribute('aria-hidden', 'true');
        this.sticky.innerHTML = '<div class="compare-sticky__bar gradient"><div class="compare-sticky__track absolute overflow-hidden"><div class="compare-sticky__items relative h-full"></div></div></div>';
        (this.querySelector('.product-compare-container') || this).prepend(this.sticky);
      }

      const items = this.sticky.querySelector('.compare-sticky__items');
      items.textContent = '';

      this.sticky.classList.toggle('compare-sticky--labels', this.querySelector('ul.compare-grid').classList.contains('compare-grid--labels'));

      columns.forEach(column => {
        const source = column.querySelector(':scope > .js-compare-sticky-source');
        const item = document.createElement('div');
        item.className = 'compare-sticky__item absolute';

        if (column.classList.contains('compare-grid__labels')) {
          item.classList.add('compare-sticky__item--labels', 'block-when-empty');
        } else if (column.classList.contains('grid__item--has-color-scheme')) {
          item.classList.add('compare-sticky__item--card', 'gradient', ...Array.from(column.classList).filter(name => name.startsWith('color-') || name === 'card--shadow'));
        }

        if (source) item.innerHTML = source.innerHTML;
        items.appendChild(item);
      });

      items.querySelectorAll('a, button').forEach(element => element.setAttribute('tabindex', '-1'));

      this.attachSticky();
      this.layoutSticky();
      this.updateSticky();
    }

    attachSticky() {
      const list = this.querySelector('ul.compare-grid');

      this.stickyScrollHandler = this.stickyScrollHandler || (() => {
        if (this.stickyFrame) return;

        this.stickyFrame = requestAnimationFrame(() => {
          this.stickyFrame = null;
          this.updateSticky();
        });
      });

      this.stickyListHandler = this.stickyListHandler || this.syncStickyScroll.bind(this);

      this.stickyHeaderHandler = this.stickyHeaderHandler || (event => {
        if (['top', 'max-height'].includes(event.propertyName) && event.target.closest?.('.section-header')) this.followHeader();
      });

      if (!this.stickyScrollRoot) {
        this.stickyScrollRoot = this.closest('.popup-modal__content') || window;
        this.stickyScrollRoot.addEventListener('scroll', this.stickyScrollHandler, { passive: true });

        if (this.stickyScrollRoot === window) document.addEventListener('transitionrun', this.stickyHeaderHandler);
      }

      if (this.stickyList !== list) {
        this.stickyList?.removeEventListener('scroll', this.stickyListHandler);
        this.stickyList = list;
        if (!stickyFollowsScroll) list?.addEventListener('scroll', this.stickyListHandler, { passive: true });

        // A scrollbar appearing in a popup narrows the columns without a window resize
        this.stickyResizeObserver = this.stickyResizeObserver || new ResizeObserver(() => this.layoutSticky());
        this.stickyResizeObserver.disconnect();
        if (list) this.stickyResizeObserver.observe(list);
      }
    }

    detachSticky() {
      this.stickyScrollRoot?.removeEventListener('scroll', this.stickyScrollHandler);
      this.stickyList?.removeEventListener('scroll', this.stickyListHandler);
      document.removeEventListener('transitionrun', this.stickyHeaderHandler);
      this.stickyResizeObserver?.disconnect();
      cancelAnimationFrame(this.stickyFrame);
      cancelAnimationFrame(this.headerFrame);

      this.stickyScrollRoot = null;
      this.stickyList = null;
      this.stickyFrame = null;
      this.headerFrame = null;

      this.sticky?.remove();
      this.sticky = null;
    }

    // The header animates its own height and position but only records them once it's done, so the bar
    // tracks its edge until the header has settled and its recorded height matches again
    followHeader() {
      const header = document.querySelector('.section-header');
      if (!header || !this.sticky) return;

      this.headerFollowUntil = performance.now() + 500;
      if (this.headerFrame) return;

      const step = () => {
        if (!this.sticky || performance.now() > this.headerFollowUntil) {
          this.headerFrame = null;
          this.sticky?.style.removeProperty('top');
          return;
        }

        this.sticky.style.top = `${Math.max(0, header.getBoundingClientRect().bottom) - 1}px`;
        this.headerFrame = requestAnimationFrame(step);
      };

      this.headerFrame = requestAnimationFrame(step);
    }

    layoutSticky() {
      if (!this.sticky || !this.stickyList) return;

      const list = this.stickyList;
      const bar = this.sticky.firstElementChild;
      const track = bar.firstElementChild;
      const listRect = list.getBoundingClientRect();

      // Rects include the scale transform popups animate with, layout sizes don't
      const scale = list.offsetWidth ? listRect.width / list.offsetWidth : 1;
      const originLeft = listRect.left + list.clientLeft * scale;

      track.style.width = `${list.offsetWidth}px`;
      track.style.left = `${(listRect.left - bar.getBoundingClientRect().left) / scale}px`;

      const items = track.firstElementChild.children;
      const columns = Array.from(list.children);

      columns.forEach((column, index) => {
        if (!items[index]) return;

        const rect = column.getBoundingClientRect();
        const isLabels = column.classList.contains('compare-grid__labels');

        // The label column is sticky, so it's placed where it sits and not where it scrolls to
        items[index].style.left = `${(rect.left - originLeft) / scale + (isLabels ? 0 : list.scrollLeft)}px`;
        items[index].style.width = `${rect.width / scale}px`;
      });

      const productColumns = columns.filter(column => !column.classList.contains('compare-grid__labels'));

      if (productColumns[1]) {
        const first = productColumns[0].getBoundingClientRect();
        const gap = Math.abs(productColumns[1].getBoundingClientRect().left - first.left) - first.width;
        track.style.setProperty('--compare-sticky-gap', `${gap / scale}px`);
      }

      this.sticky.style.setProperty('--compare-scroll-range', `${list.scrollWidth - list.clientWidth}px`);
      this.syncStickyScroll();
    }

    syncStickyScroll() {
      const items = this.sticky?.querySelector('.compare-sticky__items');
      if (!items || stickyFollowsScroll) return;

      const scrollLeft = this.stickyList.scrollLeft;
      items.style.transform = `translateX(${-scrollLeft}px)`;

      const labels = items.querySelector('.compare-sticky__item--labels');
      if (labels) labels.style.transform = `translateX(${scrollLeft}px)`;
    }

    updateSticky() {
      if (!this.sticky || !this.stickyList) return;

      const trigger = this.stickyList.querySelector(':scope > li > .compare-cell--product_summary')
        || this.stickyList.querySelector(':scope > li > .compare-cell');
      if (!trigger) return;

      const bar = this.sticky.firstElementChild;
      const stickyTop = this.sticky.getBoundingClientRect().top;
      const barBottom = stickyTop + bar.offsetHeight;

      const triggerRect = trigger.getBoundingClientRect();
      const isVisible = triggerRect.top + triggerRect.height / 2 < barBottom
        && this.stickyList.getBoundingClientRect().bottom > barBottom + bar.offsetHeight;

      if (isVisible && !this.sticky.classList.contains('compare-sticky--visible')) {
        this.layoutSticky();
      }

      this.sticky.classList.toggle('compare-sticky--visible', isVisible);
    }
  }

  customElements.define('product-compare', ProductCompare);
}
